import {
  faBriefcase,
  faMapMarkerAlt,
  faPencilPaintbrush,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import ErrorBoundary from '../lib/ErrorBoundary/ErrorBoundary';
import SafeString from '../lib/SafeString/SafeString';
import SingularOrPluralText from '../lib/SingularOrPluralText/SingularOrPluralText';

interface IOfferDetailsSectionProps {
  title?: string;
  className?: string;

  loading?: boolean;

  locationLabel?: string;
  location?: React.ReactNode;

  dateLabel?: string;
  date?: React.ReactNode;

  servicesLabel?: string;
  services?: Array<[string, string]>;
}

const OfferDetailsSection: React.FC<React.PropsWithChildren<IOfferDetailsSectionProps>> = ({
  title,
  locationLabel,
  location,
  dateLabel,
  date,
  servicesLabel,
  services,
  className,
  loading,
}) => {
  return (
    <div className={className}>
      <h2 className="text-2xl leading-[1.5em] mb-7 lg:text-4xl">{title}</h2>
      <ul className="grid gap-10 lg:text-lg">
        <DetailItem
          loading={loading}
          icon={faMapMarkerAlt}
          label={locationLabel}
          item={
            <span className="font-semibold text-lg lg:text-2xl leading-[1.5em]">
              {location}
            </span>
          }
        />

        <DetailItem
          loading={loading}
          icon={faBriefcase}
          label={dateLabel}
          item={
            <span className="text-lg lg:text-2xl leading-[1.5em] font-semibold">
              {date}
            </span>
          }
        />

        <DetailItem
          loading={loading}
          icon={faPencilPaintbrush}
          label={servicesLabel}
          item={
            <>
              <div className="text-lg lg:text-2xl leading-[1.5em] mb-1.5 font-semibold">
                {services?.length}
                {'x '}
                <SingularOrPluralText
                  number={services?.length || 0}
                  singular="Person"
                  plural="People"
                />
              </div>
              <div className="lg:text-xl">
                {services?.map(([person, service], key) => (
                  <div key={key}>
                    {person} {service}
                  </div>
                ))}
              </div>
            </>
          }
        />
      </ul>
    </div>
  );
};
export default OfferDetailsSection;

const DetailItem = ({ icon, label, item, loading = false }) => {
  return (
    <ErrorBoundary>
      <li className="flex space-x-3.5 items-center">
        <div className="grid flex-none w-12 h-12 rounded-full bg-gray-F9 place-items-center">
          <FontAwesomeIcon icon={icon} className="text-xl text-primary" />
        </div>
        <div hidden={loading}>
          <p className="text-gray-5D mb-2.5">{label}</p>
          <div>{item}</div>
        </div>

        {loading && (
          <div className="grid w-full gap-5 animate-pulse">
            <p className="h-4 bg-gray-200 rounded-full" />
            <p className="bg-gray-200 rounded-full h-7" />
          </div>
        )}
      </li>
    </ErrorBoundary>
  );
};
